// frontend/src/components/CustomMessages/CustomMessagesPage.js

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Paper,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import axios from "axios";
import { showSnackbar, showSuccessSnackbar } from "../snackbarSlice";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const CustomMessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingMessage, setEditingMessage] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    fetchMessages();
    fetchUserGrowthData();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get("/api/custom-messages/");
      setMessages(response.data.messages || []);
    } catch (error) {
      dispatch(showSnackbar("Failed to load messages"));
    }
  };

  const fetchUserGrowthData = async () => {
    try {
      const response = await axios.get("/api/user-growth/");
      setUserGrowthData(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        dispatch(showSnackbar("Failed to load user growth data"));
      }
    }
  };

  const handleAddMessage = async () => {
    if (messages.length >= 5) {
      dispatch(showSnackbar("Maximum 5 messages allowed"));
      return;
    }
    if (!newMessage.trim()) {
      dispatch(showSnackbar("Message cannot be empty"));
      return;
    }

    try {
      const updatedMessages = [...messages, newMessage];
      await axios.put("/api/custom-messages/", {
        messages: updatedMessages,
      });
      setMessages(updatedMessages);
      setNewMessage("");
      dispatch(showSuccessSnackbar("Message added successfully"));
    } catch (error) {
      console.error("Failed to add message:", error);
      dispatch(
        showSnackbar(error.response?.data?.error || "Failed to add message")
      );
    }
  };

  const handleDeleteMessage = async (index) => {
    try {
      const updatedMessages = messages.filter((_, i) => i !== index);
      await axios.put("/api/custom-messages/", {
        messages: updatedMessages, // Send the full messages array
      });
      setMessages(updatedMessages);
      dispatch(showSuccessSnackbar("Message deleted successfully"));
    } catch (error) {
      console.error("Failed to delete message:", error);
      dispatch(
        showSnackbar(error.response?.data?.error || "Failed to delete message")
      );
    }
  };

  const handleStartEdit = (index, message) => {
    setEditingIndex(index);
    setEditingMessage(message);
  };

  const handleEditChange = (value) => {
    setEditingMessage(value);
  };

  const handleSaveEdit = async (index) => {
    try {
      const updatedMessages = [...messages];
      updatedMessages[index] = editingMessage;
      await axios.put("/api/custom-messages/", {
        messages: updatedMessages, // Send the full messages array
      });
      setMessages(updatedMessages);
      dispatch(showSuccessSnackbar("Messages updated successfully"));
      setEditingIndex(null);
      setEditingMessage("");
    } catch (error) {
      console.error("Failed to update messages:", error);
      dispatch(
        showSnackbar(error.response?.data?.error || "Failed to update messages")
      );
    }
  };

  const handleDeleteConversations = async () => {
    try {
      await axios.delete("/api/conversations/delete");
      dispatch(showSuccessSnackbar("Conversations deleted successfully"));
      setOpenDialog(false);
    } catch (error) {
      dispatch(showSnackbar("Failed to delete conversations"));
      setOpenDialog(false);
    }
  };

  return (
    <>
      <Typography variant="body2" color="#888" paragraph>
        Configure up to 5 messages that will be automatically sent when you send
        Smoothspeak an image in the app.
      </Typography>

      <Paper sx={{ mb: 3, p: 2, bgcolor: "#1C1C1E" }}>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Enter new message"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#2C2C2E",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#3A3A3C",
                },
                "&.Mui-focused": {
                  backgroundColor: "#3A3A3C",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#38383A",
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleAddMessage}
            disabled={messages.length >= 5}
            startIcon={<AddIcon />}
            sx={{
              bgcolor: "#F53D3B",
              "&:hover": {
                bgcolor: "#FF453A",
              },
            }}
          >
            Add
          </Button>
        </Box>

        <List>
          {messages.map((message, index) => (
            <ListItem
              key={index}
              sx={{
                mb: 1,
                pr: 16, // Add right padding to make room for the icons
              }}
            >
              <TextField
                fullWidth
                multiline // Enable multiple lines
                minRows={1} // Start with one row
                maxRows={4} // Allow up to 4 rows before scrolling
                value={editingIndex === index ? editingMessage : message}
                onChange={(e) =>
                  editingIndex === index
                    ? handleEditChange(e.target.value)
                    : handleStartEdit(index, e.target.value)
                }
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#2C2C2E",
                    color: "#FFFFFF",
                    "&:hover": {
                      backgroundColor: "#3A3A3C",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#3A3A3C",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#38383A",
                  },
                }}
              />
              <ListItemSecondaryAction>
                {editingIndex === index ? (
                  <IconButton
                    edge="end"
                    aria-label="save"
                    onClick={() => handleSaveEdit(index)}
                    sx={{ color: "#4CAF50", ml: 1 }}
                  >
                    <SaveIcon />
                  </IconButton>
                ) : null}
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleDeleteMessage(index)}
                  sx={{ color: "#FF453A", ml: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Button
          variant="contained"
          color="error"
          onClick={() => setOpenDialog(true)}
          sx={{
            bgcolor: "#F53D3B",
            "&:hover": {
              bgcolor: "#FF453A",
            },
          }}
        >
          Delete All Conversations In App
        </Button>
      </Box>

      {userGrowthData.length > 0 && (
        <Box sx={{ mt: 4, mb: 12 }}>
          <Typography variant="h6" gutterBottom sx={{ color: "#F53D3B" }}>
            User Growth Over Time
          </Typography>
          <Paper
            sx={{
              p: 2,
              bgcolor: "#1C1C1E",
              height: { xs: 300, sm: 300 },
              width: "100%",
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={userGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#38383A" />
                <XAxis dataKey="date" stroke="#888" tick={{ fill: "#888" }} />
                <YAxis
                  stroke="#888"
                  tick={{ fill: "#888" }}
                  tickFormatter={(value) => {
                    if (value >= 1000) {
                      return `${(value / 1000).toFixed(0)}k`;
                    }
                    return value;
                  }}
                  width={45}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#2C2C2E",
                    border: "none",
                    color: "#FFFFFF",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="total_users"
                  stroke="#F53D3B"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Box>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          style: {
            backgroundColor: "#1C1C1E",
            color: "#FFFFFF",
          },
        }}
      >
        <DialogTitle>Delete All Conversations?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#888" }}>
            Are you sure you want to delete all conversations in the app? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} sx={{ color: "#888" }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConversations}
            sx={{
              color: "#FF453A",
              "&:hover": {
                bgcolor: "rgba(255, 69, 58, 0.1)",
              },
            }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomMessagesPage;
